import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';

import {
    Box,
    ExExercise,
    ICardData,
    Lesson,
    LessonPrivilegeSetting,
    StudentLessonPrivilegeSetting,
    Answer,
    Pupil,
    Habirint,
    Profile
} from '@df/interfaces';
import { CardSettings } from '@df/models';
import {
    COMMENTS,
    DECREASE_OPERATION,
    EXERCISE_CALCULATION,
    EXERCISE_LOGIC,
    EXERCISE_MAGIC_BOX,
    EXERCISE_SEQUENCE,
    EXERCISE_TASK,
    EXTRA_EXERCISE,
    EXERCISE_PUZZLE_TASK,
    EXERCISE_PUZZLE_EXAMPLE,
    EXERCISE_INSERT,
    INCREASE_OPERATION,
    MAIN_DOMAIN,
    TYPE_TRANSACTION,
    LESSON_IN_WORK
} from '@app/app.constants';

import { CountingService } from '@app/core/services/counting.service';
import { TaskService } from '@app/core/services/task.service';
import { PrivilegeService } from '@app/core/services/privilege.service';

import { BsModalService, ModalDirective } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';
import { MatDialog } from '@angular/material/dialog';
import { HabirintService } from '@app/core/services';
import { HandlerService } from '@app/_services/handler.service';
import { FormGroup, FormBuilder } from '@angular/forms';
import { AchievementsService } from '@results/services/achievements.service';

@Component({
    selector: 'app-cards',
    templateUrl: './cards.component.html',
    styleUrls: ['./cards.component.scss']
})
export class CardsComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @Input() activeCard;
    @Input() exExercise: ExExercise;
    @Input() lesson: Lesson;
    @Input() profile: Profile;
    @Input() lessonPrivilegeSettings: LessonPrivilegeSetting[];
    @Input() hasKairosBonus: boolean;
    @Input() students: Array<Pupil>;
    @Input() student: Pupil;

    @Output() clearCard = new EventEmitter();
    @Output() firstCheckCard = new EventEmitter();
    @Output() updateLessonStatus = new EventEmitter<Lesson>();

    @ViewChild('firstModal', { static: true }) firstModal: ModalDirective;
    @ViewChild('secondModal', { static: true }) secondModal: ModalDirective;
    @ViewChild('templateErrorMsg', { static: true }) templateErrorMsg: TemplateRef<any>;

    errorMsg: string = '';
    sendAnswerErrorMsg: string = `
        Ответы небыли отправленны, служба поддержки уже получила уведомление.
        Повторите попытку и если сново возникнет это окно, то попробуйте отправить
        другой вариант. Если проблема осталась ожидайте решение проблемы.`;
    getCardDataErrorMsg: string = `
        Такого варианта не существует либо данные необходимые для нормальной работы
        варианта не были получены. Пожалуйсто, перезагрузите страницу и попробуйте снова`;

    correctExercises = [];
    modalHidden: boolean = true;
    answers: Answer[] = [];
    showAttemptMessage = null;
    result;
    correctExerciseCount: number;
    logic: string = EXERCISE_LOGIC;
    calculation: string = EXERCISE_CALCULATION;
    magic_box: string = EXERCISE_MAGIC_BOX;
    task: string = EXERCISE_TASK;
    sequence: string = EXERCISE_SEQUENCE;
    puzzle_task: string = EXERCISE_PUZZLE_TASK;
    puzzle_example: string = EXERCISE_PUZZLE_EXAMPLE;
    insert: string = EXERCISE_INSERT;
    amount: number;
    isExtraExerciseActive = false;
    isShowExExerciseBtn = false;
    cardResultId: number;
    extraExercise = EXTRA_EXERCISE;
    typeTransaction = TYPE_TRANSACTION;
    countExerciseCorrect = 0;
    countExerciseInCorrect = 0;

    modalRef: BsModalRef;
    cardSettings: CardSettings;
    mainDomain: string = MAIN_DOMAIN;
    riskCharacterIsSelected = false;
    studentPrivileges: StudentLessonPrivilegeSetting[] = [];
    studentActivePrivileges: LessonPrivilegeSetting[] = [];
    isAuthorCard = true;
    isAccessCard: boolean;

    dialogRef: any;
    coachForm: FormGroup;

    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(
        private taskService: TaskService,
        private cdr: ChangeDetectorRef,
        private countingService: CountingService,
        private modalService: BsModalService,
        private privSvc: PrivilegeService,
        private habirintService: HabirintService,
        private achievementsService: AchievementsService,
        private handlerService: HandlerService,
        private fb: FormBuilder,
        public dialog: MatDialog
    ) {}

    ngOnInit(): void {
        localStorage.setItem('activeCardId', String(this.activeCard.id));
        localStorage.setItem('studentId', String(this.student.id));

        this.coachForm = this.fb.group({
            coachItem: [this.students[0].id]
        });

        this.updateData();
    }

    ngOnChanges(): void {
        const cacheCardId = localStorage.getItem('activeCardId');
        const cacheStudentId = localStorage.getItem('studentId');
        if (this.activeCard.id !== Number(cacheCardId) || this.student.id !== Number(cacheStudentId)) {
            localStorage.setItem('activeCardId', this.activeCard.id);
            localStorage.setItem('studentId', String(this.student.id));

            this.updateData();
        }
    }

    openDialog(): void {
        this.dialogRef = this.dialog.open(this.templateErrorMsg, {
            width: '50%',
            height: '245px'
        });
    }

    closeDialog(): void {
        this.dialogRef.close();
    }

    private updateData(): void {
        this.answers = [];
        this.studentActivePrivileges = [];
        this.correctExercises = this.activeCard.exercises.slice();

        if (this.activeCard.prev_results) {
            this.cardResultId = Number(this.activeCard.prev_results.card_result_id);
            if (!this.activeCard.prev_results.correct) {
                this.correctExercises = [];
            }
        } else {
            this.cardResultId = NaN;
        }

        for (const exercise of this.activeCard.exercises) {
            const answer: Answer = {
                student: this.student.id,
                fullAnswer: exercise.answers ? exercise.answers[0].fullAnswer : '',
                card: this.activeCard.id,
                shortAnswer: exercise.answers ? exercise.answers[0].shortAnswer : '',
                exercise: exercise.id,
                correct: true,
                question: exercise.answers ? exercise.answers[0].question : ''
            };
            this.answers.push(answer);
        }
        this.correctExerciseCount = this.correctExercises.length;

        this.privSvc.privilegeSettingSelected.subscribe(() => {
            this.getCardData();
            this.getStudentPrivilegeForBox();
        });

        this.getStudentPrivilegeForBox();
        this.getCardData();
        this.checkAuthorCard();
    }

    private updateUsedPrivileges(): void {
        if (!this.student.user) {
            return;
        }
        const userId = this.student.user.id;
        const { characters } = this.cardSettings;

        if (characters.length) {
            this.achievementsService.updateUsedPrivileges(userId, characters).subscribe(resp => {
                this.handlerService.checkErrors(resp);
            });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
    }

    ngAfterViewInit(): void {
        this.cdr.detectChanges();
    }

    allExercisesCorrect(): boolean {
        this.countExerciseCorrect = this.correctExercises.length;
        this.countExerciseInCorrect = this.activeCard.exercises.length - this.countExerciseCorrect;
        const isCorrect = this.activeCard.exercises.length === this.correctExercises.length;
        if (!isCorrect) {
            this.isShowExExerciseBtn = false;
            this.isExtraExerciseActive = false;
        }
        return isCorrect;
    }

    openModal(template: TemplateRef<any>, config = {}): void {
        this.modalRef = this.modalService.show(template, config);
    }

    closeModal(): void {
        this.modalRef.hide();
    }

    sendAnswers(isToAccount = true) {
        this.sendHabirintData();
        this.firstCheckCard.emit(null);
        const allCorrect = this.allExercisesCorrect();
        const card_cost = this.cardSettings.cost;
        const activeCardId = this.activeCard.id;
        let penalty = 0;
        let typeTransaction = this.typeTransaction.TRANSACTION_TYPE_BANK;
        let amount = this.cardSettings.reward;

        if (!isToAccount && this.riskCharacterIsSelected) {
            typeTransaction = this.typeTransaction.TRANSACTION_TYPE_CASH;
        }
        if (this.hasKairosBonus) {
            amount += this.cardSettings.kairos_bonus;
        }
        if (!allCorrect) {
            penalty = this.cardSettings.penalty;
        }
        this.modalHidden = true;
        this.rollBackTransaction();
        const cardData: ICardData = {
            card: activeCardId,
            student: this.student.id,
            earned_amount: amount && allCorrect ? amount : 0,
            penalty: penalty,
            cost: card_cost,
            transaction_type: typeTransaction,
            tasks_time: 0,
            answers: this.answers
        };

        this.taskService
            .setCardResults(cardData)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                resp => {
                    if (resp) {
                        this.answers = [];
                        if (card_cost > 0) {
                            if (this.lesson.payment_for_task_with_cash) {
                                this.taskService
                                    .moneyOperation(
                                        this.student.id,
                                        card_cost,
                                        INCREASE_OPERATION,
                                        COMMENTS.BUY_WITH_CASH,
                                        activeCardId
                                    )
                                    .subscribe(() => {
                                        this.taskService
                                            .moneyOperation(
                                                this.student.id,
                                                card_cost,
                                                DECREASE_OPERATION,
                                                COMMENTS.BUY_WITH_CASH,
                                                activeCardId
                                            )
                                            .pipe(takeUntil(this.destroy$))
                                            .subscribe();
                                    });
                            } else {
                                this.taskService
                                    .moneyOperation(
                                        this.student.id,
                                        card_cost,
                                        DECREASE_OPERATION,
                                        COMMENTS.BUY,
                                        activeCardId
                                    )
                                    .pipe(takeUntil(this.destroy$))
                                    .subscribe();
                            }
                        }
                        if (allCorrect) {
                            if (isToAccount) {
                                setTimeout(() => {
                                    this.taskService
                                        .moneyOperation(
                                            this.student.id,
                                            amount,
                                            INCREASE_OPERATION,
                                            COMMENTS.REWARD,
                                            activeCardId
                                        )
                                        .pipe(takeUntil(this.destroy$))
                                        .subscribe();
                                }, 2500);
                            } else {
                                setTimeout(() => {
                                    this.taskService
                                        .moneyOperation(
                                            this.student.id,
                                            amount,
                                            INCREASE_OPERATION,
                                            COMMENTS.REWARD,
                                            activeCardId
                                        )
                                        .subscribe(() => {
                                            this.taskService
                                                .moneyOperation(
                                                    this.student.id,
                                                    amount,
                                                    DECREASE_OPERATION,
                                                    COMMENTS.REWARD_WITH_CASH,
                                                    activeCardId
                                                )
                                                .pipe(takeUntil(this.destroy$))
                                                .subscribe();
                                        });
                                }, 2500);
                            }
                        } else {
                            if (this.riskCharacterIsSelected) {
                                setTimeout(() => {
                                    this.taskService
                                        .moneyOperation(
                                            this.student.id,
                                            penalty,
                                            DECREASE_OPERATION,
                                            COMMENTS.PENALTY,
                                            activeCardId
                                        )
                                        .pipe(takeUntil(this.destroy$))
                                        .subscribe();
                                }, 1000);
                            }
                        }

                        this.correctExercises = [];
                        this.showAttemptMessage = null;
                        this.clearCard.emit(null);

                        this.updateUsedPrivileges();

                        setTimeout(() => {
                            if (this.isShowExExerciseBtn) {
                                if (isToAccount && this.isExtraExerciseActive) {
                                    this.taskService
                                        .moneyOperation(
                                            this.student.id,
                                            this.exExercise.reward,
                                            INCREASE_OPERATION,
                                            COMMENTS.EX_EXERCISE_REWARD,
                                            activeCardId
                                        )
                                        .pipe(takeUntil(this.destroy$))
                                        .subscribe();
                                }
                                if (!isToAccount && this.isExtraExerciseActive) {
                                    this.taskService
                                        .moneyOperation(
                                            this.student.id,
                                            this.exExercise.reward,
                                            INCREASE_OPERATION,
                                            COMMENTS.EX_EXERCISE_REWARD,
                                            activeCardId
                                        )
                                        .subscribe(() => {
                                            this.taskService
                                                .moneyOperation(
                                                    this.student.id,
                                                    this.exExercise.reward,
                                                    DECREASE_OPERATION,
                                                    COMMENTS.EX_EXERCISE_REWARD,
                                                    activeCardId
                                                )
                                                .pipe(takeUntil(this.destroy$))
                                                .subscribe();
                                        });
                                }
                                if (this.isExtraExerciseActive) {
                                    this.updateCardResult(activeCardId, this.extraExercise.SOLVED_CORRECT);
                                } else {
                                    this.updateCardResult(activeCardId, this.extraExercise.SOLVED_INCORRECT);
                                }
                            }
                        }, 3000);
                    }
                },
                error => {
                    console.log(error);
                    this.errorMsg = this.sendAnswerErrorMsg;
                    this.openDialog();
                }
            );
        this.lesson.status = LESSON_IN_WORK;
        this.updateLessonStatus.emit(this.lesson);
    }

    exerciseCalcAnswered(answer: Answer) {
        for (const answ of this.answers) {
            if (answer.exercise === answ.exercise) {
                answ.correct = answer.correct;
                answ.fullAnswer = answer.fullAnswer;
                answ.shortAnswer = answer.shortAnswer;
            }
        }
        this.changeCorrectExercise(answer);
    }

    getExerciseById(exercise_id: number) {
        for (const exercise of this.activeCard.exercises) {
            if (exercise_id === exercise.id) {
                return exercise;
            }
        }
        return null;
    }

    questionExerciseCorrect($event) {
        if (!$event) {
            this.correctExerciseCount--;
        } else if (this.correctExerciseCount < this.activeCard.exercises.length) {
            this.correctExerciseCount++;
        }
    }

    questionExerciseAnswers($event, exercise) {
        for (const answ of this.answers) {
            for (const rcvdAnsw of $event) {
                if (answ.exercise === rcvdAnsw.exercise) {
                    answ.shortAnswer = rcvdAnsw.shortAnswer;
                    answ.fullAnswer = rcvdAnsw.fullAnswer;
                    answ.correct = rcvdAnsw.correct;
                }
            }
        }

        for (const answ of $event) {
            if (answ.exercise === exercise.id) {
                this.changeCorrectExercise(answ);
            }
        }
    }

    changeCorrectExercise(answer: Answer) {
        const exercise = this.getExerciseById(answer.exercise);
        if (exercise == null) {
            return;
        }
        if (!answer.correct) {
            if (this.correctExercises.indexOf(exercise) !== -1) {
                this.correctExercises.splice(this.correctExercises.indexOf(exercise), 1);
            }
        } else {
            if (this.correctExercises.length < this.activeCard.exercises.length) {
                if (
                    exercise.type === EXERCISE_CALCULATION ||
                    exercise.type === EXERCISE_SEQUENCE ||
                    EXERCISE_MAGIC_BOX
                ) {
                    this.correctExercises.push(exercise);
                } else {
                    if (exercise.questions.length === this.getCorrectQuestionAnswersCount(answer.exercise)) {
                        this.correctExercises.push(exercise);
                    }
                }
            }
        }
    }

    getCorrectQuestionAnswersCount(exercise_id: number): number {
        let count: number = 0;
        for (const answer of this.answers) {
            if (answer.exercise === exercise_id && answer.correct) {
                count++;
            }
        }
        return count;
    }

    private getCardData(): void {
        const data = {
            student_id: this.student.id,
            card_id: this.activeCard.id,
            risk: this.riskCharacterIsSelected,
            card_result_id: this.cardResultId
        };

        this.countingService
            .getCardSettings(data)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                resp => {
                    this.cardSettings = resp;
                    this.isAccessCard = resp.attempts >= 1;

                    if (!this.activeCard.prev_results) {
                        this.isAccessCard = true;
                    }
                },
                // @ts-ignore-start: параметор "error" небыл прочитан
                error => {
                    // @ts-ignore-end
                    this.errorMsg = this.getCardDataErrorMsg;
                    this.openDialog();
                }
            );
    }

    characterSelected(risk: boolean): void {
        this.riskCharacterIsSelected = risk;
        this.getCardData();
    }

    private getStudentPrivilegeForBox(): void {
        this.privSvc
            .getStudentLessonPrivilegeSettings(this.student.id, this.lesson.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe((response: StudentLessonPrivilegeSetting[]) => {
                this.studentPrivileges = response;
                this.studentActivePrivileges = [];
                this.getStudentActivePrivileges();
            });
    }

    private getStudentActivePrivileges(): void {
        const box: Box = this.activeCard.box;
        for (const boxPrivilege of box.privileges) {
            for (const studentPrivilege of this.studentPrivileges) {
                if (boxPrivilege === studentPrivilege.settings) {
                    const index = this.lessonPrivilegeSettings.findIndex(
                        setting => setting.id === studentPrivilege.settings
                    );
                    this.studentActivePrivileges.push(this.lessonPrivilegeSettings[index]);
                }
            }
        }
    }

    private rollBackTransaction(del = false): void {
        const current_user = `${this.profile.user.first_name} ${this.profile.user.last_name}`;
        this.taskService
            .rollBackTransactions(this.activeCard.id, current_user, del)
            .pipe(takeUntil(this.destroy$))
            .subscribe();
    }

    checkAuthorCard() {
        const prevRes = this.activeCard.prev_results;
        if (!!prevRes && prevRes.student.id !== this.student.id) {
            this.isAuthorCard = false;
            this.isAccessCard = false;
            return;
        }
        this.isAuthorCard = true;
        this.isAccessCard = true;
    }

    deleteCard(): void {
        this.clearCard.emit(null);
        const delete_card = true;
        this.rollBackTransaction(delete_card);

        this.taskService
            .deleteCard(this.activeCard.id, this.cardResultId)
            .pipe(takeUntil(this.destroy$))
            .subscribe();
    }

    clickExExercise() {
        this.isShowExExerciseBtn = !this.isShowExExerciseBtn;
    }

    updateCardResult(cardId, isExtraExercise) {
        this.taskService
            .updateCardResult(cardId, isExtraExercise)
            .pipe(takeUntil(this.destroy$))
            .subscribe();
    }

    private sendHabirintData(): void {
        const data: Habirint = {
            student_id: this.student.id,
            coach_id: Number(this.coachForm.value.coachItem),
            lesson_id: this.lesson.id,
            level: Number(this.activeCard.box.level),
            card_id: this.activeCard.id
        };
        this.habirintService.createHabirint(data).subscribe();
    }
}
